import { listHeadersQueryOptions, useCloneListHeaderMutation, useCreateListHeaderMutation, useDeleteListHeaderMutation, useRefreshListHeaderMutation } from '@/api/listQueryOptions';
import { listHeaderSchema, ListHeaderType, ListScopeType } from '@/api/lists';
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions';
import { ApiErrorComponent } from '@/components/ApiErrorComponent';
import CreateDetailAccordion, { useCreateDetail } from '@/components/detail/CreateDetailRecord';
import useBasicGridColumns from '@/components/grid/BasicGridColumns';
import { useColumnState } from '@/components/hooks/useColumnState';
import { usePersistedFilterModel } from '@/components/hooks/usePersistedFilterModel';
import { usePersistedSortModel } from '@/components/hooks/usePersistedSortModel';
import { usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel';
import useResponsive from '@/components/hooks/useResponsive';
import ListTypeAutocomplete from '@/components/ListTypeAutocomplete';
import LoadProgress from '@/components/LoadProgress';
import { ScoregQuickFilter } from '@/components/mui-x/datagrid/filter/ScoregQuickFilter';
import GridToolbarPaginationButton from '@/components/mui-x/toolbar/GridToolbarPaginationButton';
import { RenderActive } from '@/components/renderer/RenderActive';
import { getMember, getOrganization } from '@/store/auth/authSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import FilterListIcon from '@mui/icons-material/FilterList';
import PivotTableChartIcon from '@mui/icons-material/PivotTableChart';
import ShareIcon from '@mui/icons-material/Share';
import UpdateIcon from '@mui/icons-material/Update';
import { Badge, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { DataGridPremium, FilterPanelTrigger, getGridBooleanOperators, GridActionsCellItem, GridCellParams, GridColDef, GridColumnVisibilityModel, GridFilterModel, GridListViewColDef, GridLogicOperator, GridPagination, GridPaginationModel, GridRenderCellParams, GridRowId, GridRowSelectionModel, GridSortModel, GridToolbarColumnsButton, Toolbar, ToolbarButton, useGridApiRef } from '@mui/x-data-grid-premium';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { t } from 'i18next';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useSelector } from 'react-redux';

export const Route = createFileRoute('/_auth/lists')({
    component: ListHeadersComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function ListHeadersComponent() {

    const sessionOrganization = useSelector(getOrganization)
    const sessionMember = useSelector(getMember)

    const initialFilterModel = {
        items: [{ id: 'active', field: 'active', operator: 'is', value: 'true' }],
    }

    const [filterModel, setFilterModel] = usePersistedFilterModel('lists', initialFilterModel)
    const handleFilterModelChange = React.useCallback((model: GridFilterModel) => {
        setFilterModel(model);
    }, [setFilterModel]);

    const filterActive = filterModel.items.some(item => item.field === 'active' && item.operator === 'is' && item.value === true);

    const listInstancesQuery = useSuspenseQuery(listHeadersQueryOptions(!filterActive))
    const listInstances = listInstancesQuery.data;

    const navigate = useNavigate();

    const onCloned = (data: ListHeaderType) => {
        enqueueSnackbar(t('action.cloned', { entity: t('entity.listHeader') }), { variant: 'success' });
        navigate({
            from: '/lists',
            to: "/list/$listHeaderUuid",
            params: {
                listHeaderUuid: data.uuid,
            }
        })
    }

    const cloneListHeaderMutation = useCloneListHeaderMutation(onCloned)
    const refreshListHeaderMutation = useRefreshListHeaderMutation(() => {
        enqueueSnackbar(t('action.refreshed', { entity: t('entity.listHeader') }), { variant: 'success' });
    })

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState({} as ListHeaderType);

    const handleDeleteClick = (id: GridRowId, row: ListHeaderType) => () => {
        console.log('handleDeleteClick on ' + id);
        setSelectedRow(row);
        setDialogOpen(true);
    };

    const deleteMutation = useDeleteListHeaderMutation(
        () => {
            enqueueSnackbar(t('action.deleted', { entity: t('entity.listHeader') }), { variant: 'success' });
        },
        () => {
            enqueueSnackbar(t('error.default'), { variant: 'error' });
        }
    )

    const handleDelete = () => {
        if (selectedRow) {
            console.log('handleDelete ' + selectedRow.uuid);
            setDialogOpen(false);
            deleteMutation.mutate(selectedRow.uuid);
        }
    }

    const handleCancel = () => {
        setDialogOpen(false);
    }

    const basicGridColumns = useBasicGridColumns()
    const columns: GridColDef<ListHeaderType>[] = [];

    function scopesAsChips(scopes: ListScopeType[]) {
        return (scopes.map((scope) => {
            if (scope.section) {
                return (
                    <Tooltip title={scope.section.name} key={scope.section.uuid}>
                        <Chip color="secondary" style={{ backgroundColor: scope.section.color }} label={scope.section.abbreviation} sx={{ mt: 0.2, mr: 0.2 }} size="small" />
                    </Tooltip>

                )
            } else if (scope.job) {
                return (
                    <Tooltip title={scope.job.name} key={scope.job.uuid}>
                        <Chip color="secondary" style={{ backgroundColor: scope.job.color }} label={scope.job.abbreviation} sx={{ mt: 0.2, mr: 0.2 }} size="small" />
                    </Tooltip>
                )
            }
        }))
    }

    columns.push(
        {
            field: 'code',
            headerName: t('property.general.code'),
            renderCell: (params: GridCellParams) => {
                return (
                    <span>
                        {params.row.organization.uuid == sessionOrganization.uuid ? <></> :
                            <>
                                <Tooltip title={params.row.organization.name}>
                                    <ShareIcon />
                                </Tooltip>
                                &nbsp;
                            </>}
                        {params.row.code}
                    </span>
                )
            },
            cellClassName: 'scoreg-link'
        },
        {
            field: 'name',
            headerName: t('property.general.name'),
            renderCell: (params: GridCellParams) => {
                return (
                    <span>
                        {params.row.name}
                    </span>
                )
            },
            valueGetter: (_value, row) => {
                return row.name
            },
            cellClassName: 'scoreg-link'
        },
        {
            field: 'type',
            headerName: t('property.listHeader.type'),
        },
        {
            field: 'listScopes',
            headerName: t('property.listHeader.scope'),
            renderCell: (params: GridCellParams) => {
                if (params.row.listScopes && params.row.listScopes.length > 0) {
                    const scopes = params.row.listScopes as ListScopeType[]
                    return <span>{scopesAsChips(scopes)}</span>
                }
                return <></>
            },
            filterable: false
        },
        {
            field: 'refresh',
            headerName: t('property.listHeader.refresh'),
            type: 'boolean',
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridCellParams) => {
                return (
                    <UpdateIcon style={{ color: params.row.refresh == true ? 'black' : 'lightgray' }} />
                )
            }
        },
        {
            field: 'refreshDate',
            headerName: t('property.listHeader.refreshDate'),
            type: 'date',
            valueGetter: (value) => value && new Date(value),
        }
    )

    columns.push(basicGridColumns.startDateColumn, basicGridColumns.endDateColumn)

    columns.push(
        {
            field: 'active',
            headerName: t('property.general.active'),
            type: 'boolean',
            headerAlign: 'center',
            valueGetter: (_value, row) => {
                return row.rank === 1 && row.active
            },

            renderCell: RenderActive,
            filterOperators: getGridBooleanOperators().filter(operator => operator.value != 'false'),
        })

    const handleCloneClick = (row: any, includeMember: boolean, includeMemberDetails: boolean) => () => {
        cloneListHeaderMutation.mutate({ uuid: row.uuid, includeMember, includeMemberDetails })
    }

    const handleRefreshClick = (row: any) => () => {
        refreshListHeaderMutation.mutate(row.uuid)
    }

    columns.push(
        {
            field: 'cloneAction',
            type: 'actions',
            headerName: 'Listenaktionen',
            getActions: ({ id, row }: { id: GridRowId, row: any }) => {
                return [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title={t('action.listHeader.clone')}>
                                <FileCopyIcon />
                            </Tooltip>}
                        label={t('action.listHeader.clone')}
                        key={`clone-${id}`}
                        disabled={!row.permissions?.some((perm: string) => perm === "write")}
                        onClick={handleCloneClick(row, false, false)}
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title={t('action.listHeader.cloneWithMember')}>
                                <ContentCopyIcon />
                            </Tooltip>}
                        label={t('action.listHeader.cloneWithMember')}
                        key={`cloneWithMember-${id}`}
                        disabled={!row.permissions?.some((perm: string) => perm === "write")}
                        onClick={handleCloneClick(row, true, false)}
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title={t('action.listHeader.cloneWithMemberDetails')}>
                                <CopyAllIcon />
                            </Tooltip>}
                        label={t('action.listHeader.cloneWithMemberDetails')}
                        key={`cloneWithMemberDetails-${id}`}
                        disabled={!row.permissions?.some((perm: string) => perm === "write")}
                        onClick={handleCloneClick(row, true, true)}
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title={t('action.listHeader.refresh')}>
                                <UpdateIcon />
                            </Tooltip>}
                        label={t('action.listHeader.refresh')}
                        key={`refresh-${id}`}
                        disabled={!(row.permissions?.some((perm: string) => perm === "write") && row.refresh == true)}
                        onClick={handleRefreshClick(row)}
                    />
                ]
            },
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Aktionen',
            width: 100,
            getActions: ({ id, row }: { id: GridRowId, row: ListHeaderType }) => {

                return [
                    <GridActionsCellItem
                        icon={<Tooltip title={t('action.deleteRecord')}><DeleteIcon /></Tooltip>}
                        label={t('action.delete')}
                        key={`delete-${id}`}
                        disabled={!row.permissions?.some((perm: string) => perm === "delete")}
                        onClick={handleDeleteClick(id, row)}
                        color="inherit"
                    />
                ]
            },
        }
    )

    const theme = useTheme();
    const isListView = useMediaQuery(theme.breakpoints.down('sm'));

    function ListViewCell(params: GridRenderCellParams) {
        return (
            <Stack
                direction="row"
                sx={{
                    alignItems: 'center',
                    height: '100%',
                    gap: 2,
                }}
            >
                <Stack sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" sx={{
                        fontWeight: 500
                    }}>
                        {params.row.code} {params.row.name}
                    </Typography>
                </Stack>
                {
                    params.row.listScopes && <span>{scopesAsChips(params.row.listScopes)}</span>
                }
            </Stack>
        );
    }

    const listViewColDef: GridListViewColDef = {
        field: 'listColumn',
        renderCell: ListViewCell,
    };

    const handleCellClick = React.useCallback(
        (params: GridCellParams) => {
            console.log(params.colDef.field)
            if (['code', 'name', 'listColumn'].includes(params.colDef.field)) {
                navigate({
                    from: '/lists',
                    to: "/list/$listHeaderUuid",
                    params: {
                        listHeaderUuid: params.row.uuid,
                    }

                })
            }
        }, [navigate])

    const permissionsQuery = useSuspenseQuery(
        fetchDetailPermissionsQueryOptions('organization', sessionOrganization.uuid, [
            'ListHeader',
        ]),
    )
    const permissions = permissionsQuery.data

    const createable =
        permissions &&
        permissions.filter(
            (p) =>
                p.detail === 'ListHeader' && p.permissions.some((a) => a === 'create'),
        ).length > 0

    const onCreated = (data: ListHeaderType) => {
        enqueueSnackbar(t('action.created', { entity: t('entity.listHeader') }), { variant: 'success' });
        navigate({
            from: '/lists',
            to: "/list/$listHeaderUuid",
            params: {
                listHeaderUuid: data.uuid,
            }
        })
    }

    const createListHeaderMutation = useCreateListHeaderMutation(onCreated)

    const onSubmit = (data: ListHeaderType) => {
        createListHeaderMutation.mutate({
            code: data.code,
            name: data.name,
            type: data.type || ''
        })
    }

    const createDetail = useCreateDetail<ListHeaderType>({
        onSubmit,
        resolver: yupResolver<ListHeaderType, any, any>(listHeaderSchema),
        defaultValues: {
            uuid: 'new',
            member: {
                uuid: sessionMember.uuid,
            },
            organization: {
                uuid: sessionOrganization.uuid,
            },
        },
        title: t('action.new', { entity: t('entity.listHeader') }),
    })

    const responsive = useResponsive()

    const CustomToolbar = () => {
        return (
            <Toolbar>
                {responsive.minMedium &&
                    <>
                        <GridToolbarColumnsButton />
                        <FilterPanelTrigger
                            render={(props, state) => (
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                (<ToolbarButton {...props} color="default" render={
                                    <Button startIcon={
                                        <Badge badgeContent={state.filterCount} color="error" variant="standard">
                                            <FilterListIcon color='primary' fontSize="small" />
                                        </Badge>
                                    }>
                                        <span style={{ color: theme.palette.primary.main }}>
                                            {t('button.toolbar.filter')}
                                        </span>
                                    </Button >
                                }>
                                </ToolbarButton >)
                            )}
                        />
                        <Button startIcon={<PivotTableChartIcon />}
                            onClick={() => {
                                const uuids = [...rowSelectionModel.ids].map((id) => id.toString());

                                navigate({
                                    from: '/lists',
                                    to: '/listMemberPivot',
                                    search: {
                                        listHeaderUuids: uuids,
                                    }
                                })
                            }}
                            disabled={rowSelectionModel.ids.size === 0}>
                            Pivot-Ansicht
                        </Button>
                    </>
                }
                <ScoregQuickFilter />
                <Box sx={{ flexGrow: 1 }} />
                {
                    responsive.xlarge &&
                    <GridPagination />
                }
                {
                    (!responsive.xlarge) &&
                    <GridToolbarPaginationButton />
                }
                <Box sx={{ width: '10px' }} />
            </Toolbar>
        );
    }

    const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>({
        type: 'include',
        ids: new Set([]),
    });

    const apiRef = useGridApiRef()
    const columnsState = useColumnState('lists', apiRef, columns)

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('lists', {})
    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        setVisibilityModel(model)
    }, [setVisibilityModel]);

    const [sortModel, setSortModel] = usePersistedSortModel('lists', [])

    const handleSortModelChange = React.useCallback((model: GridSortModel) => {
        setSortModel(model);
    }, [setSortModel]);

    const calculatedPageSize = Math.floor((window.innerHeight - 220) / 35);
    const pageSize = calculatedPageSize;

    const defaultPageSizeOptions = [{ value: 10, label: '10' }, { value: 25, label: '25' }, { value: 50, label: '50' }, { value: 100, label: '100' }]
    defaultPageSizeOptions.push({ value: calculatedPageSize, 'label': 'Optimal' })

    const pageSizeOptions = Array.from(new Set(defaultPageSizeOptions))
    pageSizeOptions.sort((a, b) => a.value - b.value)
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
        page: 0,
        pageSize: pageSize,
    })

    const handlePaginationModelChange = (model: GridPaginationModel) => {
        setPaginationModel(model);
    }

    if (listInstancesQuery.isLoading || listInstancesQuery.isFetching || !listInstances) {
        return <LoadProgress />
    }
    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            <Box
                sx={{
                    width: { xs: '100%' },
                    height: { xs: '100%' }
                }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {createable && (
                        <CreateDetailAccordion {...createDetail}>
                            <Grid container spacing={2}>
                                <input
                                    type="hidden"
                                    defaultValue={sessionMember.uuid}
                                    {...createDetail.form.register('member.uuid')}
                                />
                                <input
                                    type="hidden"
                                    defaultValue={sessionOrganization.uuid}
                                    {...createDetail.form.register('organization.uuid')}
                                />
                                <input type="hidden" {...createDetail.form.register("uuid")} />
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 4
                                    }}>
                                    <TextField {...createDetail.form.register("code")} label="Code" required={true} />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 4
                                    }}>
                                    <TextField {...createDetail.form.register("name")} label="Name" required={true} />
                                </Grid>

                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 4
                                    }}>
                                    <ListTypeAutocomplete control={createDetail.form.control} setValue={createDetail.form.setValue} name='type' defaultValue={''} />

                                </Grid>
                            </Grid>
                        </CreateDetailAccordion>
                    )}
                    <DataGridPremium
                        columns={columnsState.columns}
                        listView={isListView}
                        listViewColumn={listViewColDef}
                        onColumnWidthChange={columnsState.onColumnWidthChange}
                        onColumnOrderChange={columnsState.onColumnOrderChange}
                        rows={listInstances}
                        getRowId={(row) => row.uuid}
                        density='compact'
                        slots={{ toolbar: CustomToolbar }}
                        slotProps={{
                            filterPanel: {
                                logicOperators: [GridLogicOperator.And],
                                filterFormProps: {
                                    logicOperatorInputProps: {
                                        sx: { display: 'none' }
                                    }
                                },
                                sx: { maxWidth: '95vw' }
                            },
                        }}
                        sx={{
                            '& .MuiDataGrid-cell': {
                                paddingLeft: 0.5,
                                paddingRight: 0.5,
                            },
                            '& .scoreg-link:not(.MuiDataGrid-cell--editing)': {
                                cursor: 'pointer'
                            },
                            '& .MuiDataGrid-columnHeader': {
                                paddingLeft: 0.5,
                                paddingRight: 0.5,
                            },
                            '--DataGrid-overlayHeight': '300px',
                        }}
                        onRowSelectionModelChange={(newRowSelectionModel) => {
                            setRowSelectionModel(newRowSelectionModel);
                        }}
                        rowSelectionModel={rowSelectionModel}
                        columnVisibilityModel={visibilityModel}
                        onColumnVisibilityModelChange={handleVisibilityModelChange}
                        hideFooter={true}
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterModelChange}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        disableRowGrouping={true}
                        disableAggregation={true}
                        pagination={true}
                        paginationModel={paginationModel}
                        pageSizeOptions={pageSizeOptions}
                        onPaginationModelChange={handlePaginationModelChange}
                        showToolbar
                        checkboxSelection
                        onCellClick={handleCellClick} />

                    <Dialog
                        open={dialogOpen}
                        onClose={handleCancel}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">
                            {"Eintrag löschen?"}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                Anstelle des Löschens ist das Setzen von Start- und Ende-Datum empfohlen.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCancel}>Abbrechen</Button>
                            <Button onClick={handleDelete} autoFocus variant="contained" color="error">
                                Löschen
                            </Button>
                        </DialogActions>
                    </Dialog>
                </div>
            </Box>
        </Box>
    );
}